import React from "react"
import { useState, useMemo, useCallback, useEffect } from "react"
import { Head, router } from "@inertiajs/react"
import { ArrowLeft, ArrowRight, CheckCircle, ChevronDown, FileText, Plus, Save, Send, Trash2, XCircle } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { DataTable, Td, Th, Thead, Tr } from "@/components/steel/table"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Field, LinkAction, PageHeader, StatusBadge, TileGrid } from "@/components/steel"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { formatCurrency } from "@/lib/utils"
import type { Bid, BidContact, BidSender, BidLineItem, BidMilestone, BidEstimate, BidCalculated, BidStatus } from "@/lib/bids"

// ---- Types ----

interface BuildingRow {
  id: string
  costCode: string
  L: number
  W: number
  H: number
  sqft: number
  qnty: number
  hr: number
  pricePerSqft: number
}

interface AccessoryRow {
  id: string
  costCode: string
  lf: number
  qnty: number
  hr: number
}

interface CostRow {
  id: string
  label: string
  qnty: number
  rate: number
}

type QuantityBasis = "perimeter" | "area"

interface ConsumableRow {
  id: string
  name: string
  unitCost: number
  basis: QuantityBasis
  usageRate: number
}

// ---- Defaults ----

const defaultBuildings: BuildingRow[] = [
  { id: "b1", costCode: "Steel Erection", L: 10, W: 50, H: 20, sqft: 500, qnty: 0, hr: 23, pricePerSqft: 10 },
  { id: "b2", costCode: "Wall", L: 10, W: 50, H: 20, sqft: 2400, qnty: 0, hr: 34, pricePerSqft: 6.5 },
  { id: "b3", costCode: "Roof", L: 10, W: 50, H: 0, sqft: 500, qnty: 0, hr: 32, pricePerSqft: 7 },
]

const defaultAccessories: AccessoryRow[] = [
  { id: "a1", costCode: "Gutters", lf: 20, qnty: 0, hr: 10 },
  { id: "a2", costCode: "Downspout", lf: 40, qnty: 0, hr: 10 },
  { id: "a3", costCode: "Snow Rail", lf: 40, qnty: 0, hr: 10 },
  { id: "a4", costCode: "Pipe Boots", lf: 0, qnty: 0, hr: 1 },
  { id: "a5", costCode: "Edge Trims", lf: 155, qnty: 0, hr: 10 },
]

const defaultLabor: CostRow[] = [
  { id: "l1", label: "Skilled", qnty: 8, rate: 40 },
  { id: "l2", label: "Hands", qnty: 1, rate: 30 },
]

const defaultEquipment: CostRow[] = [
  { id: "e1", label: "40' Aerial", qnty: 0, rate: 14 },
  { id: "e2", label: "12k Lull", qnty: 0, rate: 24 },
]

const defaultOverhead: CostRow[] = [
  { id: "o1", label: "Admin Hours", qnty: 2, rate: 40 },
]

const defaultPerDiem: CostRow[] = [
  { id: "pd1", label: "Food", qnty: 9, rate: 7 },
  { id: "pd2", label: "Lodging", qnty: 5, rate: 13 },
]

const defaultFuel: CostRow[] = [
  { id: "f1", label: "Equip", qnty: 0, rate: 5 },
  { id: "f2", label: "Trucks", qnty: 2, rate: 6 },
]

const defaultConsumables: ConsumableRow[] = []

// ---- Helpers ----

function numOrZero(v: string | number): number {
  const n = typeof v === "string" ? Number.parseFloat(v) : v
  return Number.isFinite(n) ? n : 0
}

function fmtNum(n: number, decimals = 2): string {
  if (n === 0) return "0"
  return n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: decimals })
}

function fmtCur(n: number): string {
  return formatCurrency(Math.round(n))
}

// ---- Editable Cell ----

function BidCell({
  value,
  onChange,
  readOnly = false,
  className = "",
  prefix = "",
}: {
  value: number
  onChange?: (v: number) => void
  readOnly?: boolean
  className?: string
  prefix?: string
}) {
  if (readOnly) {
    return (
      <td className={`px-2 py-1.5 text-right tabular-nums text-xs ${className}`}>
        {prefix}{fmtNum(value)}
      </td>
    )
  }
  return (
    <td className={`px-1 py-1 ${className}`}>
      <Input
        type="number"
        step="any"
        value={value || ""}
        onChange={(e) => onChange?.(numOrZero(e.target.value))}
        className="h-7 w-full rounded-none bg-secondary/50 text-right text-xs tabular-nums px-1.5 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
    </td>
  )
}

function ReadCell({ value, decimals = 2, className = "", prefix = "" }: { value: number; decimals?: number; className?: string; prefix?: string }) {
  return (
    <td className={`px-2 py-1.5 text-right tabular-nums text-xs ${className}`}>
      {prefix}{fmtNum(value, decimals)}
    </td>
  )
}

function HeaderCell({ children, className = "", colSpan }: { children: React.ReactNode; className?: string; colSpan?: number }) {
  return (
    <th colSpan={colSpan} className={`px-2 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-muted-foreground ${className}`}>
      {children}
    </th>
  )
}

// ==== Main Component ====

export default function BidEdit({ bid: existingBid }: { bid: Bid | null }) {
  const bidId = existingBid?.id

  // Contact info
  const [contact, setContact] = useState<BidContact>(
    existingBid?.contact ?? {
      clientName: "",
      companyName: "",
      email: "",
      phone: "",
      address: "",
    }
  )

  const [proposalMessage, setProposalMessage] = useState(
    existingBid?.proposalMessage ?? "We would like to thank you for the opportunity to bid on this job. We have reviewed all drawings, specs and Addenda. Please consider our proposal, and feel free to reach out with any questions. Our proposal includes the following."
  )

  const [closingMessage, setClosingMessage] = useState(
    existingBid?.closingMessage ?? "We are a labor-based company and are seeking to collaborate with general contractors who are open to utilizing third-party checks for all required materials for this project. We appreciate your consideration and look forward to the opportunity to work together."
  )

  const [sender, setSender] = useState<BidSender>(
    existingBid?.sender ?? {
      name: "Joshua Sproule",
      companyName: "Steel Structure Solutions",
      address: "P.O. Box 2 Pine City, NY 14871",
      phone: "570.418.0514",
      email: "Jsproule@steel-structure-solutions.com",
    }
  )

  const [bidName, setBidName] = useState(existingBid?.name ?? "")
  const [jobName, setJobName] = useState(existingBid?.jobName ?? "")
  const [jobLocation, setJobLocation] = useState(existingBid?.jobLocation ?? "")

  const [lineItems, setLineItems] = useState<BidLineItem[]>(
    existingBid?.lineItems ?? []
  )

  const [milestones, setMilestones] = useState<BidMilestone[]>(
    existingBid?.milestones ?? [
      { id: "ms1", label: "Deposit / Mobilization", completionPct: 0, billingPct: 10 },
      { id: "ms2", label: "Retention", completionPct: 100, billingPct: 10 },
    ]
  )

  // Schedule assumptions
  const [hrsPerDay, setHrsPerDay] = useState(existingBid?.estimate.hrsPerDay ?? 8)
  const [daysPerWeek, setDaysPerWeek] = useState(existingBid?.estimate.daysPerWeek ?? 5)
  const [weeksPerMonth, setWeeksPerMonth] = useState(existingBid?.estimate.weeksPerMonth ?? 4.333)

  // Sections
  const [buildings, setBuildings] = useState<BuildingRow[]>(existingBid?.estimate.buildings ?? defaultBuildings)
  const [accessories, setAccessories] = useState<AccessoryRow[]>(existingBid?.estimate.accessories ?? defaultAccessories)
  const [labor, setLabor] = useState<CostRow[]>(existingBid?.estimate.labor ?? defaultLabor)
  const [equipment, setEquipment] = useState<CostRow[]>(existingBid?.estimate.equipment ?? defaultEquipment)
  const [overhead, setOverhead] = useState<CostRow[]>(existingBid?.estimate.overhead ?? defaultOverhead)
  const [perDiem, setPerDiem] = useState<CostRow[]>(existingBid?.estimate.perDiem ?? defaultPerDiem)
  const [fuel, setFuel] = useState<CostRow[]>(existingBid?.estimate.fuel ?? defaultFuel)
  const [consumables, setConsumables] = useState<ConsumableRow[]>(existingBid?.estimate.consumables ?? defaultConsumables)
  const [insurance, setInsurance] = useState(existingBid?.estimate.insurance ?? 3000)
  const [overheadAdder, setOverheadAdder] = useState(existingBid?.estimate.overheadAdder ?? 0)

  // Schedule-driven inputs (for the simplified estimator)
  const [startDate, setStartDate] = useState(existingBid?.startDate ?? "")
  const [endDate, setEndDate] = useState(existingBid?.endDate ?? "")
  const [riskFactor, setRiskFactor] = useState(existingBid?.riskFactor ?? 1.15)
  const [margin, setMargin] = useState(existingBid?.margin ?? 0.20)
  const [needsPerDiem, setNeedsPerDiem] = useState(existingBid?.needsPerDiem ?? false)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [skilledCount, setSkilledCount] = useState(existingBid?.skilledParts ?? 2)
  const [handsCount, setHandsCount] = useState(existingBid?.handsParts ?? 1)
  const [ratioSkilled, setRatioSkilled] = useState(existingBid?.skilledParts ?? 2)
  const [ratioHands, setRatioHands] = useState(existingBid?.handsParts ?? 1)

  const savedBidId = bidId
  const [saveMessage, setSaveMessage] = useState("")

  // ---- Update helpers ----
  const updateBuilding = useCallback((id: string, field: keyof BuildingRow, val: number) => {
    setBuildings((prev) => {
      const pass1 = prev.map((r) => {
        if (r.id !== id) return r
        const u = { ...r, [field]: val }
        u.sqft = u.costCode === "Wall" ? (2 * u.L + 2 * u.W) * u.H : u.L * u.W
        return u
      })
      const main = pass1.find((r) => r.costCode === "Steel Erection")
      if (!main || main.id !== id) return pass1
      return pass1.map((r) => {
        if (r.costCode === "Wall") {
          return { ...r, L: main.L, W: main.W, H: main.H, sqft: (2 * main.L + 2 * main.W) * main.H }
        }
        if (r.costCode === "Roof") {
          return { ...r, L: main.L, W: main.W, sqft: main.L * main.W }
        }
        return r
      })
    })
  }, [])

  const updateAccessory = useCallback((id: string, field: keyof AccessoryRow, val: number) => {
    setAccessories((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: val } : r)))
  }, [])

  // Cascade Steel Erection dims → accessory LF values
  React.useEffect(() => {
    const main = buildings.find((r) => r.costCode === "Steel Erection")
    if (!main) return
    const { L, H } = main
    setAccessories((prev) => prev.map((r) => {
      switch (r.costCode) {
        case "Gutters":     return { ...r, lf: L * 2 }
        case "Downspout":   return { ...r, lf: Math.round(H / 10) * 10 }
        case "Snow Rail":   return { ...r, lf: L * 4 }
        case "Edge Trims":  return { ...r, lf: L * 6 }
        default:            return r
      }
    }))
  }, [buildings])

  // Cascade equipment qty → fuel Equip qty
  React.useEffect(() => {
    const equipCount = equipment
      .filter((r) => r.label === "40' Aerial" || r.label === "12k Lull")
      .reduce((s, r) => s + r.qnty, 0)
    setFuel((prev) => prev.map((r) => r.label === "Equip" ? { ...r, qnty: equipCount } : r))
  }, [equipment])

  const updateCostRow = useCallback(
    (setter: React.Dispatch<React.SetStateAction<CostRow[]>>) =>
      (id: string, field: keyof CostRow, val: number | string) => {
        setter((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: val } : r)))
      },
    []
  )

  const addCostRow = useCallback(
    (setter: React.Dispatch<React.SetStateAction<CostRow[]>>, prefix: string) => () => {
      setter((prev) => [...prev, { id: `${prefix}${Date.now()}`, label: "", qnty: 0, rate: 0 }])
    },
    []
  )

  const removeCostRow = useCallback(
    (setter: React.Dispatch<React.SetStateAction<CostRow[]>>) => (id: string) => {
      setter((prev) => prev.filter((r) => r.id !== id))
    },
    []
  )

  const updateConsumable = useCallback((id: string, field: keyof ConsumableRow, val: number | string) => {
    setConsumables((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: val } : r)))
  }, [])

  const addConsumable = useCallback(() => {
    setConsumables((prev) => [...prev, { id: `c${Date.now()}`, name: "", unitCost: 0, basis: "area" as QuantityBasis, usageRate: 0 }])
  }, [])

  const removeConsumable = useCallback((id: string) => {
    setConsumables((prev) => prev.filter((r) => r.id !== id))
  }, [])

  // ---- Calculations ----
  const calc = useMemo(() => {
    const hrsPerWeek = hrsPerDay * daysPerWeek
    const hrsPerMonth = hrsPerWeek * weeksPerMonth

    const buildingCalc = buildings.map((r) => {
      const sqft = r.costCode === "Wall"
        ? (2 * r.L + 2 * r.W) * r.H
        : r.L * r.W
      const dayRate = r.hr * hrsPerDay
      const weekRate = dayRate * daysPerWeek
      const monthRate = weekRate * weeksPerMonth
      const manHrs = r.hr > 0 && r.qnty > 0 ? (sqft * r.qnty) / r.hr : 0
      const weeks = weekRate > 0 && r.qnty > 0 ? (sqft * r.qnty) / weekRate : 0
      const pctMo = monthRate > 0 && r.qnty > 0 ? (sqft * r.qnty) / monthRate : 0
      const years = pctMo / 12
      return { ...r, sqft, dayRate, weekRate, monthRate, manHrs, weeks, pctMo, years }
    })

    const accCalc = accessories.map((r) => {
      const dayRate = r.hr * hrsPerDay
      const weekRate = dayRate * daysPerWeek
      const monthRate = weekRate * weeksPerMonth
      const totalLf = r.lf * r.qnty
      const manHrs = r.hr > 0 && r.qnty > 0 ? totalLf / r.hr : 0
      const weeks = weekRate > 0 && r.qnty > 0 ? totalLf / weekRate : 0
      const pctMo = monthRate > 0 && r.qnty > 0 ? totalLf / monthRate : 0
      const years = pctMo / 12
      return { ...r, dayRate, weekRate, monthRate, manHrs, weeks, pctMo, years }
    })

    const totalManHrs = buildingCalc.reduce((s, r) => s + r.manHrs, 0) + accCalc.reduce((s, r) => s + r.manHrs, 0)
    const totalWeeks = buildingCalc.reduce((s, r) => s + r.weeks, 0) + accCalc.reduce((s, r) => s + r.weeks, 0)
    const totalPctMo = buildingCalc.reduce((s, r) => s + r.pctMo, 0) + accCalc.reduce((s, r) => s + r.pctMo, 0)
    const totalYears = totalPctMo / 12

    const totalSqFt = buildings.reduce((s, r) => s + r.sqft * r.qnty, 0)
    const totalPerimeter = buildings.reduce((s, r) => s + 2 * (r.L + r.W) * r.qnty, 0)

    const consumableCalc = consumables.map((c) => {
      const derivedQty = c.basis === "perimeter" ? totalPerimeter : totalSqFt
      const quantity = derivedQty * c.usageRate
      const lineCost = quantity * c.unitCost
      return { ...c, derivedQty, quantity, lineCost }
    })

    const consumablesTotal = consumableCalc.reduce((s, r) => s + r.lineCost, 0)

    const regularHrsPerDay = Math.min(hrsPerDay, 8)
    const otHrsPerDay = Math.max(hrsPerDay - 8, 0)
    const weekdayEffHrs = regularHrsPerDay + otHrsPerDay * 1.5
    const weekdayDays = Math.min(daysPerWeek, 5)
    const weekendDays = Math.max(daysPerWeek - 5, 0)
    const weeklyEffHrs = weekdayDays * weekdayEffHrs + weekendDays * hrsPerDay * 1.5

    let bizDays: number | null = null
    if (startDate && endDate) {
      const s = new Date(startDate), e = new Date(endDate)
      if (e > s) {
        let cnt = 0
        const cur = new Date(s)
        while (cur <= e) {
          if (cur.getDay() !== 0 && cur.getDay() !== 6) cnt++
          cur.setDate(cur.getDate() + 1)
        }
        bizDays = cnt
      }
    }

    function calcCostSection(rows: CostRow[]) {
      return rows.map((r) => {
        const daily = r.qnty * r.rate * weekdayEffHrs
        const weekly = r.qnty * r.rate * weeklyEffHrs
        const monthly = weekly * weeksPerMonth
        const total = bizDays != null
          ? r.qnty * r.rate * weekdayEffHrs * bizDays
          : weekly * totalWeeks
        const mnHrs = bizDays != null ? r.qnty * hrsPerDay * bizDays : null
        const durationWeeks = bizDays != null ? bizDays / daysPerWeek : totalWeeks
        const durationPctMo = durationWeeks / weeksPerMonth
        const durationYears = durationPctMo / 12
        return { ...r, daily, weekly, monthly, total, mnHrs, durationWeeks, durationPctMo, durationYears }
      })
    }

    const laborCalc = calcCostSection(labor)
    const equipCalc = calcCostSection(equipment)
    const overheadCalc = calcCostSection(overhead)
    const perDiemCalc = calcCostSection(perDiem)
    const fuelCalc = calcCostSection(fuel)

    const laborTotal = laborCalc.reduce((s, r) => s + r.total, 0)
    const equipTotal = equipCalc.reduce((s, r) => s + r.total, 0)
    const overheadTotal = overheadCalc.reduce((s, r) => s + r.total, 0)
    const perDiemTotal = perDiemCalc.reduce((s, r) => s + r.total, 0)
    const fuelTotal = fuelCalc.reduce((s, r) => s + r.total, 0)

    const grandTotal = laborTotal + equipTotal + overheadTotal + (needsPerDiem ? perDiemTotal : 0) + fuelTotal + consumablesTotal + insurance + overheadAdder

    return {
      hrsPerWeek,
      hrsPerMonth,
      buildingCalc,
      accCalc,
      totalManHrs,
      totalWeeks,
      totalPctMo,
      totalYears,
      totalSqFt,
      totalPerimeter,
      consumableCalc,
      consumablesTotal,
      laborCalc,
      equipCalc,
      overheadCalc,
      perDiemCalc,
      fuelCalc,
      laborTotal,
      equipTotal,
      overheadTotal,
      perDiemTotal,
      fuelTotal,
      grandTotal,
      bizDays,
    }
  }, [buildings, accessories, consumables, labor, equipment, overhead, perDiem, fuel, insurance, overheadAdder, hrsPerDay, daysPerWeek, weeksPerMonth, needsPerDiem, startDate, endDate])

  // Schedule-driven summary: crew size from date range, margin on revenue
  const scheduleCalc = useMemo(() => {
    if (!startDate || !endDate) return null
    const start = new Date(startDate)
    const end = new Date(endDate)
    if (end <= start) return null

    let businessDays = 0
    const cur = new Date(start)
    while (cur <= end) {
      const d = cur.getDay()
      if (d !== 0 && d !== 6) businessDays++
      cur.setDate(cur.getDate() + 1)
    }

    const workHours = businessDays * hrsPerDay
    if (workHours === 0) return null

    const adjustedHours = calc.totalManHrs * riskFactor
    const cost = calc.grandTotal
    const rawPrice = cost / (1 - margin)
    const floorPrice = buildings.reduce((s, r) => {
      const sqft = r.costCode === "Wall" ? (2 * r.L + 2 * r.W) * r.H : r.L * r.W
      return s + sqft * r.qnty * (r.pricePerSqft ?? 0)
    }, 0)
    const price = Math.max(rawPrice, floorPrice)
    const profit = price - cost

    return { businessDays, workHours, adjustedHours, cost, profit, price, floorPrice, floorActive: floorPrice > rawPrice }
  }, [startDate, endDate, riskFactor, margin, calc.totalManHrs, calc.grandTotal, hrsPerDay, buildings])

  // Guard: when crew effect drives endDate, skip the reverse recalc
  const endDateSetByCrew = React.useRef(false)

  // Crew counts → end date via inverse Brooks-law: businessDays = adjHrs / (√totalCrew × hrsPerDay)
  React.useEffect(() => {
    const total = skilledCount + handsCount
    if (total === 0 || !startDate || calc.totalManHrs === 0) return
    const adjustedHours = calc.totalManHrs * riskFactor
    const businessDaysNeeded = Math.ceil(adjustedHours / (Math.sqrt(total) * hrsPerDay))
    const date = new Date(startDate)
    let remaining = businessDaysNeeded
    while (remaining > 0) {
      date.setDate(date.getDate() + 1)
      if (date.getDay() !== 0 && date.getDay() !== 6) remaining--
    }
    endDateSetByCrew.current = true
    setEndDate(date.toISOString().split("T")[0])
  }, [skilledCount, handsCount, startDate, calc.totalManHrs, riskFactor, hrsPerDay])

  function splitByRatio(total: number) {
    const rTotal = ratioSkilled + ratioHands
    const newSkilled = rTotal > 0 ? Math.max(1, Math.round(total * ratioSkilled / rTotal)) : total
    return { newSkilled, newHands: Math.max(0, total - newSkilled) }
  }

  // End date → crew count: inverse Brooks-law: totalCrew = (adjHrs / (businessDays × hrsPerDay))²
  React.useEffect(() => {
    if (endDateSetByCrew.current) { endDateSetByCrew.current = false; return }
    if (!startDate || !endDate || calc.totalManHrs === 0) return
    const start = new Date(startDate)
    const end = new Date(endDate)
    if (end <= start) return
    let businessDays = 0
    const cur = new Date(start)
    while (cur <= end) {
      if (cur.getDay() !== 0 && cur.getDay() !== 6) businessDays++
      cur.setDate(cur.getDate() + 1)
    }
    if (businessDays === 0) return
    const adjustedHours = calc.totalManHrs * riskFactor
    const totalNeeded = Math.ceil((adjustedHours / (businessDays * hrsPerDay)) ** 2)
    const { newSkilled, newHands } = splitByRatio(totalNeeded)
    setSkilledCount(newSkilled)
    setHandsCount(newHands)
  }, [endDate])

  // Snap suggestions: exact finish dates for crew sizes around current total
  const snapSuggestions = React.useMemo(() => {
    if (!startDate || calc.totalManHrs === 0) return []
    const adjHrs = calc.totalManHrs * riskFactor
    const currentTotal = Math.max(1, skilledCount + handsCount)
    const sizes = Array.from(new Set([
      Math.max(1, currentTotal - 1),
      currentTotal,
      currentTotal + 1,
    ]))
    return sizes.map((total) => {
      const daysNeeded = Math.ceil(adjHrs / (Math.sqrt(total) * hrsPerDay))
      const d = new Date(startDate)
      let rem = daysNeeded
      while (rem > 0) {
        d.setDate(d.getDate() + 1)
        if (d.getDay() !== 0 && d.getDay() !== 6) rem--
      }
      const snapDate = d.toISOString().split("T")[0]
      const isCurrent = total === currentTotal
      const isExact = snapDate === endDate
      return { total, snapDate, isCurrent, isExact }
    })
  }, [startDate, skilledCount, handsCount, calc.totalManHrs, riskFactor, hrsPerDay, endDate])

  // Sync crew counts into labor rows
  React.useEffect(() => {
    setLabor((prev) => prev.map((row, i) => {
      if (i === 0) return { ...row, qnty: skilledCount }
      if (i === 1) return { ...row, qnty: handsCount }
      return row
    }))
  }, [skilledCount, handsCount])

  // Auto-generate milestones from date range: deposit 10% + monthly 80% + retention 10%
  React.useEffect(() => {
    if (!startDate || !endDate) return
    const months = Math.max(1, Math.ceil(
      (new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24 * 30.44)
    ))
    const monthPct = Math.round((80 / months) * 10) / 10
    const generated: BidMilestone[] = [
      { id: "ms-dep", label: "Deposit / Mobilization", completionPct: 0, billingPct: 10 },
      ...Array.from({ length: months }, (_, i) => ({
        id: `ms-m${i + 1}`,
        label: `Month ${i + 1}`,
        completionPct: Math.round(((i + 1) / months) * 100),
        billingPct: i === months - 1 ? Math.round((80 - monthPct * (months - 1)) * 10) / 10 : monthPct,
      })),
      { id: "ms-ret", label: "Retention", completionPct: 100, billingPct: 10 },
    ]
    setMilestones(generated)
  }, [startDate, endDate])

  // Cascade crew → per diem quantities
  React.useEffect(() => {
    const food = skilledCount + handsCount
    const lodging = Math.ceil(skilledCount - 1 + handsCount) + 1
    setPerDiem((prev) => prev.map((r) => {
      if (r.label === "Food")    return { ...r, qnty: food }
      if (r.label === "Lodging") return { ...r, qnty: lodging }
      return r
    }))
  }, [skilledCount, handsCount])

  // ---- Save / Update bid ----
  const handleSave = useCallback(() => {
    const estimate: BidEstimate = {
      hrsPerDay,
      daysPerWeek,
      weeksPerMonth,
      buildings,
      accessories,
      labor,
      equipment,
      overhead,
      perDiem,
      fuel,
      consumables,
      insurance,
      overheadAdder,
    }

    const calculated: BidCalculated = {
      totalManHrs: calc.totalManHrs,
      totalWeeks: calc.totalWeeks,
      totalSqFt: calc.totalSqFt,
      totalPerimeter: calc.totalPerimeter,
      laborTotal: calc.laborTotal,
      equipTotal: calc.equipTotal,
      overheadTotal: calc.overheadTotal,
      perDiemTotal: calc.perDiemTotal,
      fuelTotal: calc.fuelTotal,
      consumablesTotal: calc.consumablesTotal,
      grandTotal: calc.grandTotal,
      price: scheduleCalc?.price ?? 0,
      profit: scheduleCalc?.profit ?? 0,
    }

    const bidData = {
      name: bidName,
      sender,
      contact,
      estimate,
      calculated,
      proposalMessage,
      closingMessage,
      lineItems,
      milestones,
      jobName,
      jobLocation,
      startDate,
      endDate,
      riskFactor,
      margin,
      skilledParts: skilledCount,
      handsParts: handsCount,
      needsPerDiem,
    }

    if (savedBidId) {
      router.patch(`/bids/${savedBidId}`, bidData as any, {
        preserveScroll: true,
        onSuccess: () => setSaveMessage("Bid updated"),
      })
    } else {
      router.post("/bids", { ...bidData, status: "draft" } as any)
    }

    setTimeout(() => setSaveMessage(""), 2000)
  }, [bidName, sender, contact, proposalMessage, closingMessage, lineItems, milestones, jobName, jobLocation, hrsPerDay, daysPerWeek, weeksPerMonth, buildings, accessories, labor, equipment, overhead, perDiem, fuel, consumables, insurance, overheadAdder, calc, scheduleCalc, savedBidId, startDate, endDate, riskFactor, margin, skilledCount, handsCount, needsPerDiem])

  const handleViewProposal = useCallback(() => {
    if (savedBidId) router.visit(`/bids/${savedBidId}/proposal`)
  }, [savedBidId])

  const setStatus = useCallback((status: BidStatus) => {
    if (savedBidId) router.patch(`/bids/${savedBidId}`, { status }, { preserveScroll: true })
  }, [savedBidId])

  const [convertOpen, setConvertOpen] = useState(false)
  const [convertStartDate, setConvertStartDate] = useState("")
  const [convertEndDate, setConvertEndDate] = useState("")

  const handleConvert = useCallback(() => {
    const today = new Date().toISOString().slice(0, 10)
    const inThreeMonths = new Date()
    inThreeMonths.setMonth(inThreeMonths.getMonth() + 3)
    setConvertStartDate(startDate || today)
    setConvertEndDate(endDate || inThreeMonths.toISOString().slice(0, 10))
    setConvertOpen(true)
  }, [startDate, endDate])

  const submitConvert = useCallback(() => {
    if (!savedBidId) return
    router.post(
      `/bids/${savedBidId}/convert`,
      { start_date: convertStartDate, end_date: convertEndDate },
      { preserveScroll: true, onSuccess: () => setConvertOpen(false) }
    )
  }, [savedBidId, convertStartDate, convertEndDate])

  // Clear save message on unmount
  useEffect(() => {
    return () => setSaveMessage("")
  }, [])

  // ---- Render helpers ----

  function renderCostSection(
    title: string,
    rows: { daily: number; weekly: number; monthly: number; total: number; id: string; label: string; qnty: number; rate: number; mnHrs?: number | null; durationWeeks?: number; durationPctMo?: number; durationYears?: number }[],
    rawRows: CostRow[],
    setter: React.Dispatch<React.SetStateAction<CostRow[]>>,
    sectionTotal: number,
    prefix: string
  ) {
    const update = updateCostRow(setter)
    const add = addCostRow(setter, prefix)
    const remove = removeCostRow(setter)

    return (
      <>
        <tr className="bg-secondary/30">
          <td colSpan={7} className="px-2 py-2 text-xs font-bold uppercase tracking-wider text-foreground">
            {title}
          </td>
          <td colSpan={5} />
          <td className="px-2 py-2 text-right text-xs font-semibold tabular-nums text-foreground">
            {fmtCur(sectionTotal)}
          </td>
        </tr>
        {rows.map((r, i) => (
          <tr key={r.id} className="border-b border-border/30 hover:bg-secondary/10">
            <td className="px-1 py-1">
              <Input
                value={rawRows[i].label}
                onChange={(e) => update(r.id, "label", e.target.value)}
                className="h-7 w-full bg-transparent text-xs px-1.5 border-0 focus-visible:ring-1"
              />
            </td>
            <BidCell value={rawRows[i].qnty} onChange={(v) => update(r.id, "qnty", v)} />
            <td className="px-1 py-1">
              <div className="flex items-center">
                <span className="text-[10px] text-muted-foreground mr-0.5">$</span>
                <Input
                  type="number"
                  step="any"
                  value={rawRows[i].rate || ""}
                  onChange={(e) => update(r.id, "rate", numOrZero(e.target.value))}
                  className="h-7 w-full rounded-none bg-secondary/50 text-right text-xs tabular-nums px-1.5 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
            </td>
            <ReadCell value={r.daily} />
            <ReadCell value={r.weekly} />
            <ReadCell value={r.monthly} />
            {r.mnHrs != null ? <ReadCell value={r.mnHrs} decimals={1} /> : <td />}
            <ReadCell value={r.durationWeeks ?? 0} decimals={2} />
            <ReadCell value={r.durationPctMo ?? 0} decimals={2} />
            <ReadCell value={r.durationYears ?? 0} decimals={4} />
            <ReadCell value={r.total} className="font-semibold text-foreground" />
            <td className="px-1 py-1 text-center">
              <button
                type="button"
                onClick={() => remove(r.id)}
                className="text-muted-foreground hover:text-destructive transition-colors"
                aria-label={`Remove ${r.label}`}
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </td>
            <td />
          </tr>
        ))}
        <tr>
          <td colSpan={13} className="px-2 py-1">
            <button
              type="button"
              onClick={add}
              className="flex items-center gap-1 text-[10px] text-muted-foreground transition-colors hover:text-foreground"
            >
              <Plus className="h-3 w-3" /> Add row
            </button>
          </td>
        </tr>
      </>
    )
  }

  const totalBillingPct = milestones.reduce((s, m) => s + m.billingPct, 0)
  const price = scheduleCalc?.price ?? 0
  const profit = scheduleCalc?.profit ?? 0
  const perSqft = calc.totalSqFt > 0 && price > 0 ? price / calc.totalSqFt : 0

  const costGroups: { title: string; total: number; rows: { label: string; detail: string }[] }[] = [
    { title: "Labor", total: calc.laborTotal, rows: labor.map((r) => ({ label: r.label, detail: `${r.qnty} × ${fmtCur(r.rate)}` })) },
    { title: "Equipment", total: calc.equipTotal, rows: equipment.map((r) => ({ label: r.label, detail: `${r.qnty} × ${fmtCur(r.rate)}` })) },
    { title: "Overhead", total: calc.overheadTotal, rows: overhead.map((r) => ({ label: r.label, detail: `${r.qnty} × ${fmtCur(r.rate)}` })) },
    { title: "Per diem", total: needsPerDiem ? calc.perDiemTotal : 0, rows: perDiem.map((r) => ({ label: r.label, detail: `${r.qnty} × ${fmtCur(r.rate)}` })) },
    { title: "Fuel", total: calc.fuelTotal, rows: fuel.map((r) => ({ label: r.label, detail: `${r.qnty} × ${fmtCur(r.rate)}` })) },
    { title: "Consumables", total: calc.consumablesTotal, rows: consumables.map((r) => ({ label: r.name, detail: `${r.basis} × ${fmtCur(r.unitCost)}` })) },
  ]

  return (
    <>
      <Head title={bidName || "New bid"} />

      <PageHeader
        leading={
          <Button variant="ghost" size="icon-sm" onClick={() => router.visit("/bids")}>
            <ArrowLeft />
          </Button>
        }
        title={bidName || "New bid"}
        meta={
          <span className="flex items-center gap-2.5">
            {existingBid ? <StatusBadge status={existingBid.status} /> : null}
            <span className="num">
              {savedBidId ? `${savedBidId} · Rev ${existingBid?.revision ?? 1}` : "Unsaved"}
              {contact.companyName ? ` · ${contact.companyName}` : ""}
            </span>
          </span>
        }
        actions={
          <>
            {saveMessage ? <span className="text-label text-good">{saveMessage}</span> : null}
            {savedBidId ? (
              <Button variant="outline" size="sm" onClick={handleViewProposal}>
                <FileText />
                Proposal
              </Button>
            ) : null}
            <Button size="sm" onClick={handleSave}>
              <Save />
              {savedBidId ? "Save" : "Create bid"}
            </Button>
          </>
        }
      />

      <div className="grid xl:grid-cols-[minmax(0,1fr)_296px]">
        <div className="border-border px-6 py-4 pb-7 xl:border-r">
          <FormSection title="Bid" hint="This becomes the project name when the bid converts">
            <Field label="Bid name" className="sm:col-span-2">
              <Input
                value={bidName}
                onChange={(e) => setBidName(e.target.value)}
                placeholder="Riverside Industrial Complex — Phase II"
              />
            </Field>
          </FormSection>

          <FormSection title="Client" hint="Appears on the proposal letterhead">
            <Field label="Contact">
              <Input value={contact.clientName} onChange={(e) => setContact((p) => ({ ...p, clientName: e.target.value }))} placeholder="Richard Hale" />
            </Field>
            <Field label="Company">
              <Input value={contact.companyName} onChange={(e) => setContact((p) => ({ ...p, companyName: e.target.value }))} placeholder="Acme Construction" />
            </Field>
            <Field label="Email">
              <Input type="email" value={contact.email} onChange={(e) => setContact((p) => ({ ...p, email: e.target.value }))} placeholder="name@example.com" />
            </Field>
            <Field label="Phone">
              <Input value={contact.phone} onChange={(e) => setContact((p) => ({ ...p, phone: e.target.value }))} placeholder="(555) 123-4567" />
            </Field>
            <Field label="Address" className="sm:col-span-2">
              <Input value={contact.address} onChange={(e) => setContact((p) => ({ ...p, address: e.target.value }))} placeholder="123 Main St, City, ST 12345" />
            </Field>
          </FormSection>

          <FormSection title="Sender" hint="Your company block at the top of the letter">
            <Field label="Your name">
              <Input value={sender.name} onChange={(e) => setSender((p) => ({ ...p, name: e.target.value }))} />
            </Field>
            <Field label="Company">
              <Input value={sender.companyName} onChange={(e) => setSender((p) => ({ ...p, companyName: e.target.value }))} />
            </Field>
            <Field label="Address">
              <Input value={sender.address} onChange={(e) => setSender((p) => ({ ...p, address: e.target.value }))} />
            </Field>
            <Field label="Phone">
              <Input value={sender.phone} onChange={(e) => setSender((p) => ({ ...p, phone: e.target.value }))} />
            </Field>
            <Field label="Email" className="sm:col-span-2">
              <Input value={sender.email} onChange={(e) => setSender((p) => ({ ...p, email: e.target.value }))} />
            </Field>
          </FormSection>

          <FormSection title="Job" hint="Used for the RE: line and the billing sheet">
            <Field label="Job name">
              <Input value={jobName} onChange={(e) => setJobName(e.target.value)} placeholder="Havana Glen Bath House" />
            </Field>
            <Field label="Location">
              <Input value={jobLocation} onChange={(e) => setJobLocation(e.target.value)} placeholder="Montour Falls NY" />
            </Field>
            <Field label="Start">
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </Field>
            <Field label="Finish">
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </Field>
          </FormSection>

          <section className="mb-5">
            <h2 className="text-title font-semibold">Crew</h2>
            <p className="mb-3 mt-0.5 text-meta text-muted-foreground">
              {scheduleCalc
                ? `${scheduleCalc.businessDays} business days · ${fmtNum(calc.totalManHrs, 0)} est. hours · ${fmtNum(scheduleCalc.adjustedHours, 0)} risk-adjusted`
                : "Set a start and finish date to schedule the crew"}
            </p>
            <div className="grid gap-2.5 sm:grid-cols-4">
              <Field label="Skilled">
                <Input
                  className="num"
                  type="number"
                  min="0"
                  value={skilledCount}
                  onChange={(e) => {
                    const val = Math.max(0, numOrZero(e.target.value))
                    setSkilledCount(val)
                    setRatioSkilled(Math.max(1, val))
                    setRatioHands(Math.max(0, handsCount))
                  }}
                />
              </Field>
              <Field label="Hands">
                <Input
                  className="num"
                  type="number"
                  min="0"
                  value={handsCount}
                  onChange={(e) => {
                    const val = Math.max(0, numOrZero(e.target.value))
                    setHandsCount(val)
                    setRatioSkilled(Math.max(1, skilledCount))
                    setRatioHands(Math.max(0, val))
                  }}
                />
              </Field>
              <Field label="Ratio skilled">
                <Input className="num" type="number" min="1" value={ratioSkilled} onChange={(e) => setRatioSkilled(Math.max(1, numOrZero(e.target.value)))} />
              </Field>
              <Field label="Ratio hands">
                <Input className="num" type="number" min="0" value={ratioHands} onChange={(e) => setRatioHands(Math.max(0, numOrZero(e.target.value)))} />
              </Field>
            </div>
            {snapSuggestions.length > 0 ? (
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {snapSuggestions.map(({ total, snapDate, isCurrent, isExact }) => (
                  <button
                    key={total}
                    type="button"
                    onClick={() => {
                      const { newSkilled, newHands } = splitByRatio(total)
                      endDateSetByCrew.current = true
                      setEndDate(snapDate)
                      setSkilledCount(newSkilled)
                      setHandsCount(newHands)
                    }}
                    className={`num rounded-md border px-2 py-1 text-meta transition-colors ${
                      isCurrent && isExact
                        ? "border-transparent bg-secondary text-foreground"
                        : "border-border text-muted-foreground hover:bg-secondary hover:text-foreground"
                    }`}
                  >
                    {total} crew → {new Date(`${snapDate}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    {isCurrent && !isExact ? " ⚠" : ""}
                  </button>
                ))}
              </div>
            ) : null}
          </section>

          <section className="mb-5">
            <h2 className="text-title font-semibold">Letter content</h2>
            <p className="mb-3 mt-0.5 text-meta text-muted-foreground">Shown on the proposal, editable there too</p>
            <div className="flex flex-col gap-2.5">
              <Field label="Opening">
                <Textarea rows={3} value={proposalMessage} onChange={(e) => setProposalMessage(e.target.value)} />
              </Field>
              <Field label="Closing">
                <Textarea rows={2} value={closingMessage} onChange={(e) => setClosingMessage(e.target.value)} />
              </Field>
            </div>
          </section>

          <section className="mb-5">
            <div className="mb-3 flex items-baseline justify-between">
              <div>
                <h2 className="text-title font-semibold">Proposal line items</h2>
                <p className="mt-0.5 text-meta text-muted-foreground">Scope shown on the letter with dollar amounts</p>
              </div>
              <LinkAction onClick={() => setLineItems((prev) => [...prev, { id: `li-${Date.now()}`, description: "", amount: 0 }])}>
                Add line
              </LinkAction>
            </div>
            <div className="flex flex-col gap-2">
              {lineItems.map((li) => (
                <div key={li.id} className="flex items-center gap-2">
                  <Input
                    value={li.description}
                    onChange={(e) => setLineItems((prev) => prev.map((r) => (r.id === li.id ? { ...r, description: e.target.value } : r)))}
                    placeholder="Standing seam roof including trims, gutter, snow rail and downspouts"
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    value={li.amount || ""}
                    onChange={(e) => setLineItems((prev) => prev.map((r) => (r.id === li.id ? { ...r, amount: numOrZero(e.target.value) } : r)))}
                    className="num w-36 text-right"
                  />
                  <Button variant="ghost" size="icon-sm" onClick={() => setLineItems((prev) => prev.filter((r) => r.id !== li.id))}>
                    <Trash2 />
                  </Button>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-5">
            <div className="mb-3 flex items-baseline justify-between">
              <div>
                <h2 className="text-title font-semibold">Estimated progress billing</h2>
                <p className="mt-0.5 text-meta text-muted-foreground">
                  Total billing {totalBillingPct}%
                  {totalBillingPct !== 100 ? " — should be 100%" : ""}
                </p>
              </div>
              <LinkAction onClick={() => setMilestones((prev) => [...prev, { id: `ms-${Date.now()}`, label: "", completionPct: 0, billingPct: 0 }])}>
                Add milestone
              </LinkAction>
            </div>
            <div className="flex flex-col gap-2">
              {milestones.map((ms) => (
                <div key={ms.id} className="flex items-center gap-2">
                  <Input
                    value={ms.label}
                    onChange={(e) => setMilestones((prev) => prev.map((m) => (m.id === ms.id ? { ...m, label: e.target.value } : m)))}
                    placeholder="Steel erection complete"
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    value={ms.completionPct || ""}
                    onChange={(e) => setMilestones((prev) => prev.map((m) => (m.id === ms.id ? { ...m, completionPct: numOrZero(e.target.value) } : m)))}
                    className="num w-20 text-right"
                    title="% complete"
                  />
                  <Input
                    type="number"
                    value={ms.billingPct || ""}
                    onChange={(e) => setMilestones((prev) => prev.map((m) => (m.id === ms.id ? { ...m, billingPct: numOrZero(e.target.value) } : m)))}
                    className="num w-20 text-right"
                    title="% billed"
                  />
                  <span className="num w-24 text-right text-label text-muted-foreground">
                    {fmtCur(calc.grandTotal * (ms.billingPct / 100))}
                  </span>
                  <Button variant="ghost" size="icon-sm" onClick={() => setMilestones((prev) => prev.filter((m) => m.id !== ms.id))}>
                    <Trash2 />
                  </Button>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-5">
            <h2 className="text-title font-semibold">Buildings</h2>
            <p className="mb-2.5 mt-0.5 text-meta text-muted-foreground">
              Man hours derive from square footage over the sq ft per hour rate
            </p>
            <DataTable minWidth={720}>
              <Thead>
                <Th>Cost code</Th>
                <Th align="right">L</Th>
                <Th align="right">W</Th>
                <Th align="right">H</Th>
                <Th align="right">Sq ft</Th>
                <Th align="right">Qty</Th>
                <Th align="right">Ft²/hr</Th>
                <Th align="right">$/ft²</Th>
                <Th align="right">Man hrs</Th>
              </Thead>
              <tbody>
                {calc.buildingCalc.map((r, i) => (
                  <Tr key={r.id} className="hover:bg-transparent">
                    <Td strong>{r.costCode}</Td>
                    <NumCell value={buildings[i].L} onChange={(v) => updateBuilding(r.id, "L", v)} />
                    <NumCell value={buildings[i].W} onChange={(v) => updateBuilding(r.id, "W", v)} />
                    <NumCell value={buildings[i].H} onChange={(v) => updateBuilding(r.id, "H", v)} />
                    <Td align="right" num>{fmtNum(r.sqft, 0)}</Td>
                    <NumCell value={buildings[i].qnty} onChange={(v) => updateBuilding(r.id, "qnty", v)} />
                    <NumCell value={buildings[i].hr} onChange={(v) => updateBuilding(r.id, "hr", v)} />
                    <NumCell value={buildings[i].pricePerSqft ?? 0} onChange={(v) => updateBuilding(r.id, "pricePerSqft", v)} />
                    <Td align="right" num strong>{fmtNum(r.manHrs, 1)}</Td>
                  </Tr>
                ))}
              </tbody>
            </DataTable>
          </section>

          <section className="mb-5">
            <h2 className="mb-2.5 text-title font-semibold">Accessories</h2>
            <DataTable minWidth={520}>
              <Thead>
                <Th>Cost code</Th>
                <Th align="right">LF</Th>
                <Th align="right">Qty</Th>
                <Th align="right">LF/hr</Th>
                <Th align="right">Man hrs</Th>
              </Thead>
              <tbody>
                {calc.accCalc.map((r, i) => (
                  <Tr key={r.id} className="hover:bg-transparent">
                    <Td strong>{r.costCode}</Td>
                    <NumCell value={accessories[i].lf} onChange={(v) => updateAccessory(r.id, "lf", v)} />
                    <NumCell value={accessories[i].qnty} onChange={(v) => updateAccessory(r.id, "qnty", v)} />
                    <NumCell value={accessories[i].hr} onChange={(v) => updateAccessory(r.id, "hr", v)} />
                    <Td align="right" num strong>{fmtNum(r.manHrs, 1)}</Td>
                  </Tr>
                ))}
              </tbody>
            </DataTable>
          </section>

          <section className="mb-5">
            <h2 className="mb-2.5 text-title font-semibold">Crew, equipment and overhead</h2>
            <TileGrid min={250}>
              {costGroups.map((group) => (
                <div key={group.title} className="bg-card px-3.5 py-3">
                  <div className="mb-2 flex items-baseline justify-between">
                    <span className="text-label font-semibold">{group.title}</span>
                    <span className="num text-label font-medium">{fmtCur(group.total)}</span>
                  </div>
                  {group.rows.map((row, index) => (
                    <div key={`${group.title}-${index}`} className="flex items-center justify-between border-t border-border py-1.5">
                      <span className="text-label text-muted-foreground">{row.label}</span>
                      <span className="num text-label">{row.detail}</span>
                    </div>
                  ))}
                </div>
              ))}
            </TileGrid>
          </section>

          <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
            <CollapsibleTrigger className="flex w-full items-center justify-between rounded-lg border border-border bg-card px-3.5 py-2.5 text-body font-medium transition-colors hover:bg-secondary">
              Advanced — full estimate detail
              <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${advancedOpen ? "rotate-180" : ""}`} />
            </CollapsibleTrigger>
            <CollapsibleContent className="flex flex-col gap-3 pt-3">
              <div className="flex items-end justify-end gap-2">
                <Field label="Hrs/day" className="w-20">
                  <Input className="num" type="number" value={hrsPerDay} onChange={(e) => setHrsPerDay(numOrZero(e.target.value))} />
                </Field>
                <Field label="Days/wk" className="w-20">
                  <Input className="num" type="number" value={daysPerWeek} onChange={(e) => setDaysPerWeek(numOrZero(e.target.value))} />
                </Field>
                <Field label="Wks/mo" className="w-24">
                  <Input className="num" type="number" step="0.001" value={weeksPerMonth} onChange={(e) => setWeeksPerMonth(numOrZero(e.target.value))} />
                </Field>
              </div>

      <Card className="rounded-sm border-border bg-card overflow-hidden">
        <CardHeader className="pb-1 pt-3 px-3">
          <CardTitle className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Cost Estimation Detail</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] border-collapse text-foreground">
              {/* ============ BUILDING SECTION ============ */}
              <thead>
                <tr className="bg-secondary/30">
                  <th colSpan={13} className="px-2 py-2 text-left text-xs font-bold uppercase tracking-wider text-foreground">Steel Erection</th>
                </tr>
                <tr className="border-b border-border">
                  <HeaderCell className="text-left min-w-[100px]">Cost Code</HeaderCell>
                  <HeaderCell>L</HeaderCell>
                  <HeaderCell>W</HeaderCell>
                  <HeaderCell>H</HeaderCell>
                  <HeaderCell>SqFt</HeaderCell>
                  <HeaderCell>Qnty</HeaderCell>
                  <HeaderCell>Hr</HeaderCell>
                  <HeaderCell>$/sqft floor</HeaderCell>
                  <HeaderCell>Day</HeaderCell>
                  <HeaderCell>Week</HeaderCell>
                  <HeaderCell>Month</HeaderCell>
                  <HeaderCell>M/Hrs</HeaderCell>
                  <HeaderCell>Weeks</HeaderCell>
                </tr>
              </thead>
              <tbody>
                {calc.buildingCalc.map((r, i) => {
                  const isMain = r.costCode === "Steel Erection"
                  return (
                    <tr key={r.id} className="border-b border-border/30 hover:bg-secondary/10">
                      <td className="px-2 py-1.5 text-xs font-medium text-foreground">{r.costCode}</td>
                      {isMain ? (
                        <>
                          <BidCell value={buildings[i].L} onChange={(v) => updateBuilding(r.id, "L", v)} />
                          <BidCell value={buildings[i].W} onChange={(v) => updateBuilding(r.id, "W", v)} />
                          <BidCell value={buildings[i].H} onChange={(v) => updateBuilding(r.id, "H", v)} />
                          <ReadCell value={r.sqft} decimals={0} className="text-foreground font-medium" />
                          <BidCell value={buildings[i].qnty} onChange={(v) => updateBuilding(r.id, "qnty", v)} />
                        </>
                      ) : (
                        <>
                          <BidCell value={buildings[i].L} onChange={(v) => updateBuilding(r.id, "L", v)} />
                          <BidCell value={buildings[i].W} onChange={(v) => updateBuilding(r.id, "W", v)} />
                          <BidCell value={buildings[i].H} onChange={(v) => updateBuilding(r.id, "H", v)} />
                          <ReadCell value={r.sqft} decimals={0} className="text-foreground font-medium" />
                          <BidCell value={buildings[i].qnty} onChange={(v) => updateBuilding(r.id, "qnty", v)} />
                        </>
                      )}
                      <BidCell value={buildings[i].hr} onChange={(v) => updateBuilding(r.id, "hr", v)} />
                      <BidCell value={buildings[i].pricePerSqft ?? 0} onChange={(v) => updateBuilding(r.id, "pricePerSqft", v)} prefix="$" />
                      <ReadCell value={r.dayRate} />
                      <ReadCell value={r.weekRate} />
                      <ReadCell value={r.monthRate} />
                      <ReadCell value={r.manHrs} decimals={4} className="font-semibold text-foreground" />
                      <ReadCell value={r.weeks} decimals={4} />
                    </tr>
                  )
                })}

                {/* ============ ACCESSORIES SECTION ============ */}
                <tr className="bg-secondary/30">
                  <td colSpan={13} className="px-2 py-2 text-xs font-bold uppercase tracking-wider text-foreground">
                    Accessories <span className="font-normal text-muted-foreground">(LF)</span>
                  </td>
                </tr>
                <tr className="border-b border-border">
                  <HeaderCell className="text-left">Cost Code</HeaderCell>
                  <HeaderCell>LF</HeaderCell>
                  <HeaderCell>{""}</HeaderCell>
                  <HeaderCell>{""}</HeaderCell>
                  <HeaderCell>{""}</HeaderCell>
                  <HeaderCell>Qnty</HeaderCell>
                  <HeaderCell>Hr</HeaderCell>
                  <HeaderCell>{""}</HeaderCell>
                  <HeaderCell>Day</HeaderCell>
                  <HeaderCell>Week</HeaderCell>
                  <HeaderCell>Month</HeaderCell>
                  <HeaderCell>M/Hrs</HeaderCell>
                  <HeaderCell>Weeks</HeaderCell>
                </tr>
                {calc.accCalc.map((r, i) => {
                  const autoDerived = ["Gutters", "Downspout", "Snow Rail", "Edge Trims"].includes(r.costCode)
                  return (
                    <tr key={r.id} className="border-b border-border/30 hover:bg-secondary/10">
                      <td className="px-2 py-1.5 text-xs font-medium text-foreground">{r.costCode}</td>
                      {autoDerived
                        ? <ReadCell value={accessories[i].lf} decimals={0} className="text-muted-foreground" />
                        : <BidCell value={accessories[i].lf} onChange={(v) => updateAccessory(r.id, "lf", v)} />
                      }
                      <td /><td /><td />
                      <BidCell value={accessories[i].qnty} onChange={(v) => updateAccessory(r.id, "qnty", v)} />
                      <BidCell value={accessories[i].hr} onChange={(v) => updateAccessory(r.id, "hr", v)} />
                      <td />
                      <ReadCell value={r.dayRate} />
                      <ReadCell value={r.weekRate} />
                      <ReadCell value={r.monthRate} />
                      <ReadCell value={r.manHrs} decimals={4} className="font-semibold text-foreground" />
                      <ReadCell value={r.weeks} decimals={4} />
                    </tr>
                  )
                })}

                {/* ============ PRODUCTION SUMMARY ============ */}
                <tr className="border-t-2 border-border bg-secondary">
                  <td colSpan={6} className="px-2 py-2 text-xs font-bold text-foreground">Production Total</td>
                  <td colSpan={5} />
                  <td className="px-2 py-2 text-right text-xs font-semibold tabular-nums text-foreground">{fmtNum(calc.totalManHrs, 4)}</td>
                  <td className="px-2 py-2 text-right text-xs font-semibold tabular-nums text-foreground">{fmtNum(calc.totalWeeks, 4)}</td>
                </tr>
              </tbody>

              {/* ============ CONSUMABLES / MATERIALS ============ */}
              <thead>
                <tr className="border-t-2 border-border">
                  <th colSpan={13} className="px-2 py-2 text-left text-xs font-bold uppercase tracking-wider text-foreground bg-secondary/30">
                    Consumables / Materials
                    <span className="ml-2 font-normal text-[10px] text-muted-foreground">
                      Perimeter: {fmtNum(calc.totalPerimeter, 0)} ft | Area: {fmtNum(calc.totalSqFt, 0)} sqft
                    </span>
                  </th>
                </tr>
                <tr className="border-b border-border">
                  <HeaderCell className="text-left min-w-[120px]">Material</HeaderCell>
                  <HeaderCell>Basis</HeaderCell>
                  <HeaderCell>Usage Rate</HeaderCell>
                  <HeaderCell>Unit Cost</HeaderCell>
                  <HeaderCell>Derived Qty</HeaderCell>
                  <HeaderCell>Calc Qty</HeaderCell>
                  <HeaderCell colSpan={5}>{""}</HeaderCell>
                  <HeaderCell>Line Cost</HeaderCell>
                  <HeaderCell>{""}</HeaderCell>
                </tr>
              </thead>
              <tbody>
                {calc.consumableCalc.map((r, i) => (
                  <tr key={r.id} className="border-b border-border/30 hover:bg-secondary/10">
                    <td className="px-1 py-1">
                      <Input
                        value={consumables[i].name}
                        onChange={(e) => updateConsumable(r.id, "name", e.target.value)}
                        className="h-7 w-full bg-transparent text-xs px-1.5 border-0 focus-visible:ring-1"
                      />
                    </td>
                    <td className="px-1 py-1">
                      <select
                        value={consumables[i].basis}
                        onChange={(e) => updateConsumable(r.id, "basis", e.target.value)}
                        className="h-7 w-full rounded-md border border-border bg-secondary/50 text-xs px-1.5 text-foreground"
                      >
                        <option value="perimeter">Perimeter</option>
                        <option value="area">Area</option>
                      </select>
                    </td>
                    <td className="px-1 py-1">
                      <Input
                        type="number"
                        step="any"
                        value={consumables[i].usageRate || ""}
                        onChange={(e) => updateConsumable(r.id, "usageRate", numOrZero(e.target.value))}
                        className="h-7 w-full rounded-none bg-secondary/50 text-right text-xs tabular-nums px-1.5 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </td>
                    <td className="px-1 py-1">
                      <div className="flex items-center">
                        <span className="text-[10px] text-muted-foreground mr-0.5">$</span>
                        <Input
                          type="number"
                          step="any"
                          value={consumables[i].unitCost || ""}
                          onChange={(e) => updateConsumable(r.id, "unitCost", numOrZero(e.target.value))}
                          className="h-7 w-full rounded-none bg-secondary/50 text-right text-xs tabular-nums px-1.5 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                      </div>
                    </td>
                    <ReadCell value={r.derivedQty} decimals={0} className="text-muted-foreground" />
                    <ReadCell value={r.quantity} decimals={2} className="text-foreground font-medium" />
                    <td /><td /><td /><td /><td />
                    <ReadCell value={r.lineCost} className="font-semibold text-foreground" prefix="$" />
                    <td className="px-1 py-1 text-center">
                      <button
                        type="button"
                        onClick={() => removeConsumable(r.id)}
                        className="text-muted-foreground hover:text-destructive transition-colors"
                        aria-label={`Remove ${r.name}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </td>
                  </tr>
                ))}
                <tr className="border-b border-border/30">
                  <td colSpan={11} className="px-2 py-1">
                    <button
                      type="button"
                      onClick={addConsumable}
                      className="flex items-center gap-1 text-[10px] text-muted-foreground transition-colors hover:text-foreground"
                    >
                      <Plus className="h-3 w-3" /> Add consumable
                    </button>
                  </td>
                  <td className="px-2 py-2 text-right text-xs font-semibold tabular-nums text-foreground">
                    {fmtCur(calc.consumablesTotal)}
                  </td>
                  <td />
                </tr>
              </tbody>

              {/* ============ COST SECTIONS ============ */}
              <thead>
                <tr className="border-t-2 border-border">
                  <HeaderCell className="text-left min-w-[100px]">Item</HeaderCell>
                  <HeaderCell>Qnty</HeaderCell>
                  <HeaderCell>Rate</HeaderCell>
                  <HeaderCell>Day</HeaderCell>
                  <HeaderCell>Week</HeaderCell>
                  <HeaderCell>Month</HeaderCell>
                  <HeaderCell>M/Hrs</HeaderCell>
                  <HeaderCell>Weeks</HeaderCell>
                  <HeaderCell>%/Mo</HeaderCell>
                  <HeaderCell>Years</HeaderCell>
                  <HeaderCell>Totals</HeaderCell>
                  <HeaderCell>{""}</HeaderCell>
                  <HeaderCell>{""}</HeaderCell>
                </tr>
              </thead>
              <tbody>
                {renderCostSection("Labor", calc.laborCalc, labor, setLabor, calc.laborTotal, "l")}
                {renderCostSection("Equipment", calc.equipCalc, equipment, setEquipment, calc.equipTotal, "e")}
                {renderCostSection("Overhead", calc.overheadCalc, overhead, setOverhead, calc.overheadTotal, "o")}
                {renderCostSection(
                  needsPerDiem ? "Per-Diem" : "Per-Diem (excluded — toggle above)",
                  calc.perDiemCalc, perDiem, setPerDiem,
                  needsPerDiem ? calc.perDiemTotal : 0, "pd"
                )}
                {renderCostSection("Fuel", calc.fuelCalc, fuel, setFuel, calc.fuelTotal, "f")}

                {/* ============ INSURANCE ============ */}
                <tr className="bg-secondary/30">
                  <td colSpan={7} className="px-2 py-2 text-xs font-bold uppercase tracking-wider text-foreground">Insurance</td>
                  <td colSpan={4} />
                  <td className="px-1 py-1">
                    <div className="flex items-center">
                      <span className="text-[10px] text-muted-foreground mr-0.5">$</span>
                      <Input
                        type="number"
                        value={insurance || ""}
                        onChange={(e) => setInsurance(numOrZero(e.target.value))}
                        className="h-7 w-full rounded-none bg-secondary/50 text-right text-xs tabular-nums px-1.5 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </div>
                  </td>
                  <td />
                </tr>

                {/* ============ OVERHEAD ADDER ============ */}
                <tr className="bg-secondary/30">
                  <td colSpan={7} className="px-2 py-2 text-xs font-bold uppercase tracking-wider text-foreground">
                    Overhead Adder
                    <span className="ml-2 font-normal text-[10px] text-muted-foreground">Flat amount added to bid</span>
                  </td>
                  <td colSpan={4} />
                  <td className="px-1 py-1">
                    <div className="flex items-center">
                      <span className="text-[10px] text-muted-foreground mr-0.5">$</span>
                      <Input
                        type="number"
                        value={overheadAdder || ""}
                        onChange={(e) => setOverheadAdder(numOrZero(e.target.value))}
                        className="h-7 w-full rounded-none bg-secondary/50 text-right text-xs tabular-nums px-1.5 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </div>
                  </td>
                  <td />
                </tr>

                {/* ============ PROJECTED PROFIT ============ */}
                {(() => {
                  const profit = scheduleCalc?.profit ?? (lineItems.reduce((s, li) => s + (li.amount || 0), 0) - calc.grandTotal)
                  const revenue = scheduleCalc?.price ?? lineItems.reduce((s, li) => s + (li.amount || 0), 0)
                  const marginPct = revenue > 0 ? (profit / revenue) * 100 : 0
                  const isNeg = profit < 0
                  return (
                    <tr className="  border-t-2 border-border bg-secondary">
                      <td colSpan={11} className={`px-2 py-2 text-xs font-bold uppercase tracking-wider  ${isNeg ? "text-destructive" : "text-good"}`}>
                        Projected Profit
                        {revenue > 0 && (
                          <span className="ml-2 font-normal normal-case text-muted-foreground/80">
                            ({fmtNum(marginPct, 1)}% margin on {fmtCur(revenue)} revenue)
                          </span>
                        )}
                      </td>
                      <td className={`px-2 py-2 text-right text-sm font-bold tabular-nums ${isNeg ? "text-destructive" : "text-good"}`}>
                        {fmtCur(profit)}
                      </td>
                      <td />
                    </tr>
                  )
                })()}

                {/* ============ GRAND TOTAL ============ */}
                <tr className="bg-secondary">
                  <td colSpan={11} className="px-2 py-3 text-body font-semibold uppercase tracking-wider">Final Cost Estimate</td>
                  <td className="px-2 py-3 text-right text-body font-semibold tabular-nums text-whisper">{fmtCur(calc.grandTotal)}</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

            </CollapsibleContent>
          </Collapsible>
        </div>

        <aside className="px-5 py-4 pb-7">
          <p className="eyebrow mb-2.5">Estimate</p>
          <p className="num text-[28px] font-semibold tracking-[-0.025em] text-whisper">{fmtCur(price)}</p>
          <p className="mb-4 mt-1 text-label text-muted-foreground">
            {perSqft > 0
              ? `$${perSqft.toFixed(2)} per sq ft across ${fmtNum(calc.totalSqFt, 0)} sq ft`
              : "Set dates and dimensions to price this bid"}
          </p>

          <SummaryRow label="Man hours" value={fmtNum(calc.totalManHrs, 1)} />
          <SummaryRow label="Duration" value={scheduleCalc ? `${fmtNum(calc.totalWeeks, 1)} weeks` : "—"} />
          <SummaryRow label="Square feet" value={fmtNum(calc.totalSqFt, 0)} />
          <SummaryRow label="Cost" value={fmtCur(calc.grandTotal)} />
          <SummaryRow label={`Profit at ${Math.round(margin * 100)}%`} value={fmtCur(profit)} />

          <div className="mt-4 grid grid-cols-2 gap-2.5">
            <Field label="Margin">
              <Input
                className="num text-right"
                type="number"
                min="0"
                max="99"
                value={Math.round(margin * 100)}
                onChange={(e) => setMargin(numOrZero(e.target.value) / 100)}
              />
            </Field>
            <Field label="Risk factor">
              <Input
                className="num text-right"
                type="number"
                step="0.01"
                min="1"
                max="2"
                value={riskFactor}
                onChange={(e) => setRiskFactor(numOrZero(e.target.value))}
              />
            </Field>
            <Field label="Insurance">
              <Input className="num text-right" type="number" value={insurance || ""} onChange={(e) => setInsurance(numOrZero(e.target.value))} />
            </Field>
            <Field label="Overhead adder">
              <Input className="num text-right" type="number" value={overheadAdder || ""} onChange={(e) => setOverheadAdder(numOrZero(e.target.value))} />
            </Field>
          </div>

          <div className="mt-3.5 flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2.5">
            <span className="text-body">Per diem</span>
            <Switch checked={needsPerDiem} onCheckedChange={setNeedsPerDiem} />
          </div>

          <div className="mt-4 flex flex-col gap-1.5">
            {savedBidId && existingBid?.status === "draft" ? (
              <Button size="sm" onClick={() => setStatus("sent")}>
                <Send />
                Mark sent
              </Button>
            ) : null}
            {savedBidId && existingBid?.status === "sent" ? (
              <>
                <Button size="sm" onClick={() => setStatus("accepted")}>
                  <CheckCircle />
                  Accept
                </Button>
                <Button variant="outline" size="sm" onClick={() => setStatus("rejected")}>
                  <XCircle />
                  Reject
                </Button>
              </>
            ) : null}
            {savedBidId && existingBid?.status === "accepted" ? (
              <Button size="sm" onClick={handleConvert}>
                <ArrowRight />
                Convert to project
              </Button>
            ) : null}
            {savedBidId && existingBid?.status === "converted" && existingBid.convertedProjectId ? (
              <Button variant="outline" size="sm" onClick={() => router.visit(`/projects/${existingBid.convertedProjectId!}`)}>
                <ArrowRight />
                View project
              </Button>
            ) : null}
            {savedBidId ? (
              <Button
                variant="outline"
                size="sm"
                className="text-destructive"
                onClick={() => {
                  if (window.confirm(`Delete "${bidName || "Untitled bid"}"? Estimate rows, line items and milestones go with it.`)) {
                    router.delete(`/bids/${savedBidId}`)
                  }
                }}
              >
                <Trash2 />
                Delete bid
              </Button>
            ) : null}
          </div>
        </aside>
      </div>

      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convert to a project</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2.5">
              <span className="text-label text-muted-foreground">Budget (final estimate)</span>
              <span className="num text-body font-medium">{formatCurrency(calc.grandTotal)}</span>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <Field label="Start date">
                <Input type="date" value={convertStartDate} onChange={(e) => setConvertStartDate(e.target.value)} />
              </Field>
              <Field label="End date">
                <Input type="date" value={convertEndDate} onChange={(e) => setConvertEndDate(e.target.value)} />
              </Field>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setConvertOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={submitConvert} disabled={!convertStartDate || !convertEndDate}>
              Convert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function FormSection({
  children,
  hint,
  title,
}: {
  children: React.ReactNode
  hint: string
  title: string
}) {
  return (
    <section className="mb-5">
      <h2 className="text-title font-semibold">{title}</h2>
      <p className="mb-3 mt-0.5 text-meta text-muted-foreground">{hint}</p>
      <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
    </section>
  )
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between border-t border-border py-2">
      <span className="text-label text-muted-foreground">{label}</span>
      <span className="num text-body font-medium">{value}</span>
    </div>
  )
}

function NumCell({ onChange, value }: { onChange: (value: number) => void; value: number }) {
  return (
    <td className="px-1 py-1">
      <Input
        type="number"
        step="any"
        value={value || ""}
        onChange={(e) => onChange(numOrZero(e.target.value))}
        className="num h-7 w-full px-1.5 text-right"
      />
    </td>
  )
}
